import { create } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import {
    type QueryClient,
    keepPreviousData,
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";

import type { GitpodAPI } from "@/api";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { autoPaginate } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser, keys as userQueryKeys, type PlainUser } from "@/queries/user-queries";
import {
    type Service,
    ServicePhase,
    type Task,
    type TaskExecution,
    TaskExecutionPhase,
    GetServiceRequestSchema,
    StopServiceRequestSchema,
    StartServiceRequestSchema,
    StopTaskExecutionRequestSchema,
    GetTaskRequestSchema,
    GetTaskExecutionRequestSchema,
    StartTaskRequestSchema,
} from "gitpod-next-api/gitpod/v1/environment_automation_pb";
import { ResourceOperation, ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";

export type PlainService = Service;
export type PlainTask = Task;
export type PlainTaskExecution = TaskExecution;

export const environmentAutomationQueryKey = {
    listServices: (environmentId?: string) => keyWithPrincipal(["listServices", environmentId]),
    getService: (serviceId?: string) => keyWithPrincipal(["getService", serviceId]),
    getTask: (id?: string) => keyWithPrincipal(["getTask", id]),
    getTaskExecution: (id?: string) => keyWithPrincipal(["getTaskExecution", id]),
    listTasks: (environmentId?: string) => keyWithPrincipal(["listTasks", environmentId]),
    listTaskExecutions: (environmentId?: string) => keyWithPrincipal(["listTaskExecutions", environmentId]),
};

export const handleAutomationEvents = async (
    api: GitpodAPI,
    client: QueryClient,
    evt: WatchEventsResponse,
    environmentId: string,
) => {
    if (evt.resourceType === ResourceType.TASK) {
        if (evt.operation === ResourceOperation.UPDATE) {
            const task = await refetchTask(api, evt.resourceId);
            setTaskInCache(client, environmentId, evt.resourceId, task ? task : undefined);
        }
        if (evt.operation === ResourceOperation.CREATE) {
            await client.invalidateQueries({ queryKey: environmentAutomationQueryKey.listTasks(environmentId) });
        }
        if (evt.operation === ResourceOperation.DELETE) {
            setTaskInCache(client, environmentId, evt.resourceId, undefined);
        }
    }
    if (evt.resourceType === ResourceType.SERVICE) {
        if (evt.operation === ResourceOperation.UPDATE_STATUS || evt.operation === ResourceOperation.UPDATE) {
            const service = await refetchService(api, evt.resourceId);
            setServiceInCache(client, environmentId, evt.resourceId, service ? service : undefined);
        }
        if (evt.operation === ResourceOperation.CREATE) {
            await client.invalidateQueries({ queryKey: environmentAutomationQueryKey.listServices(environmentId) });
        }
        if (evt.operation === ResourceOperation.DELETE) {
            setServiceInCache(client, environmentId, evt.resourceId, undefined);
        }
    }
    if (evt.resourceType === ResourceType.TASK_EXECUTION) {
        if (evt.operation === ResourceOperation.UPDATE_STATUS || evt.operation === ResourceOperation.UPDATE) {
            const taskExecution = await refetchTaskExecution(api, evt.resourceId);
            setTaskExectionInCache(client, environmentId, evt.resourceId, taskExecution ? taskExecution : undefined);
        }
        if (evt.operation === ResourceOperation.CREATE) {
            await client.invalidateQueries({
                queryKey: environmentAutomationQueryKey.listTaskExecutions(environmentId),
            });
        }
        if (evt.operation === ResourceOperation.DELETE) {
            setTaskExectionInCache(client, environmentId, evt.resourceId, undefined);
        }
    }
};

const refetchTask = async (api: GitpodAPI, taskId: string) => {
    try {
        const response = await api.environmentAutomationService.getTask({ id: taskId });
        return response.task;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch task", taskId);
        }
    }
};

const refetchTaskExecution = async (api: GitpodAPI, taskExecutionId: string) => {
    try {
        const response = await api.environmentAutomationService.getTaskExecution({ id: taskExecutionId });
        return response.taskExecution;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch task execution", taskExecutionId);
        }
    }
};

const refetchService = async (api: GitpodAPI, serviceId: string) => {
    try {
        const response = await api.environmentAutomationService.getService({ id: serviceId });
        return response.service;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch service", serviceId);
        }
    }
};

function getListQueryUpdaterFn<T extends { id?: string }>(id: string, item?: T) {
    return (currentData?: T[]) => {
        if (!currentData) {
            return currentData;
        }
        return currentData
            .map((p) => {
                if (p?.id !== id) {
                    return p;
                }
                return item;
            })
            .filter((w) => !!w);
    };
}

function setTaskInCache(client: QueryClient, environmentId: string, taskId: string, task?: PlainTask) {
    client.setQueryData(environmentAutomationQueryKey.getTask(taskId), task);
    client.setQueryData(environmentAutomationQueryKey.listTasks(environmentId), getListQueryUpdaterFn(taskId, task));
}

function setTaskExectionInCache(
    client: QueryClient,
    environmentId: string,
    taskExecutionId: string,
    taskExecution?: PlainTaskExecution,
) {
    client.setQueryData(environmentAutomationQueryKey.getTaskExecution(taskExecutionId), taskExecution);
    client.setQueryData(
        environmentAutomationQueryKey.listTaskExecutions(environmentId),
        getListQueryUpdaterFn(taskExecutionId, taskExecution),
    );
}

function setServiceInCache(client: QueryClient, environmentId: string, serviceId: string, service?: PlainService) {
    client.setQueryData(environmentAutomationQueryKey.getService(serviceId), service);
    client.setQueryData(
        environmentAutomationQueryKey.listServices(environmentId),
        getListQueryUpdaterFn(serviceId, service),
    );
}

export const useListServicesOptions = (environmentId?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    return queryOptions({
        queryKey: environmentAutomationQueryKey.listServices(environmentId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            if (!environmentId) {
                return [];
            }
            const services = await autoPaginate(
                (params) => api.environmentAutomationService.listServices(params),
                {
                    filter: {
                        environmentIds: [environmentId],
                    },
                },
                (response) => response.services,
            );

            return services;
        },
        enabled: !!user && !!environmentId,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });
};

export const useListServices = (environmentId?: string) => {
    return useQuery(useListServicesOptions(environmentId));
};

export const useGetServiceOptions = (serviceId?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    return queryOptions({
        queryKey: environmentAutomationQueryKey.getService(serviceId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            if (!serviceId) {
                return;
            }

            const { service } = await api.environmentAutomationService.getService(
                create(GetServiceRequestSchema, {
                    id: serviceId,
                }),
            );

            if (!service) {
                throw new Error("Service not found");
            }

            return service;
        },
        enabled: !!user && !!serviceId,
        staleTime: 60_000,
    });
};

export const useGetService = (serviceId?: string) => {
    return useQuery(useGetServiceOptions(serviceId));
};

export const useStopService = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (
            options: { serviceId: string; environmentId: string } | { reference: string; environmentId: string },
        ) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }
            let serviceId: string;
            if ("serviceId" in options) {
                serviceId = options.serviceId;
            } else {
                const response = await api.environmentAutomationService.listServices({
                    filter: {
                        environmentIds: [options.environmentId],
                        references: [options.reference],
                    },
                });
                serviceId = response.services[0]?.id;
                if (!serviceId) {
                    throw new Error(`No service found for reference "${options.reference}"`);
                }
            }
            await api.environmentAutomationService.stopService(
                create(StopServiceRequestSchema, {
                    id: serviceId,
                }),
            );
            optimisticStopService(client, serviceId, options.environmentId);
        },
    });
};

export const useStartService = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (options: { serviceId: string; environmentId: string }) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }
            await api.environmentAutomationService.startService(
                create(StartServiceRequestSchema, {
                    id: options.serviceId,
                }),
            );
            optimisticStartService(client, options.serviceId, options.environmentId);
        },
    });
};

export const useStopTaskExecution = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (options: { taskExecutionId: string; environmentId: string }) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }
            await api.environmentAutomationService.stopTaskExecution(
                create(StopTaskExecutionRequestSchema, {
                    id: options.taskExecutionId,
                }),
            );
            optimisticStopTaskExecution(client, options.taskExecutionId, options.environmentId);
        },
    });
};

export const useListTasksOptions = (environmentId?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    return queryOptions({
        queryKey: environmentAutomationQueryKey.listTasks(environmentId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            if (!environmentId) {
                return [];
            }
            const tasks = await autoPaginate(
                (params) => api.environmentAutomationService.listTasks(params),
                {
                    filter: {
                        environmentIds: [environmentId],
                    },
                },
                (response) => response.tasks,
            );

            return tasks.sort((a, b) => {
                if (isManualTask(a) && isManualTask(b)) {
                    return (a.metadata?.name || "") < (b.metadata?.name || "") ? -1 : 1;
                } else if (isManualTask(a)) {
                    return -1;
                } else if (isManualTask(b)) {
                    return 1;
                }

                return (b.metadata?.triggeredBy?.length || 0) - (a.metadata?.triggeredBy?.length || 0);
            });
        },
        enabled: !!user && !!environmentId,
        staleTime: 60_000,
    });
};

export const useListTasks = (environmentId?: string) => {
    return useQuery(useListTasksOptions(environmentId));
};

export function isManualTask(a: PlainTask) {
    const triggers = (a.metadata?.triggeredBy || []).map((t) => t.trigger.case);
    return triggers.includes("manual");
}

export const useGetTaskOptions = (id?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    return queryOptions({
        queryKey: environmentAutomationQueryKey.getTask(id),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const { task } = await api.environmentAutomationService.getTask(
                create(GetTaskRequestSchema, {
                    id,
                }),
            );
            if (!task) {
                throw new Error("Task not found");
            }

            return task;
        },
        enabled: !!user && !!id,
        staleTime: 60_000,
    });
};

export const useGetTask = (id?: string) => {
    return useQuery(useGetTaskOptions(id));
};

export const useListTaskExecutionsOptions = (environmentId: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    return queryOptions({
        queryKey: environmentAutomationQueryKey.listTaskExecutions(environmentId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            if (!environmentId) {
                return [];
            }
            const taskExecutions = await autoPaginate(
                (params) => api.environmentAutomationService.listTaskExecutions(params),
                {
                    filter: {
                        environmentIds: [environmentId],
                    },
                },
                (response) => response.taskExecutions,
            );

            return taskExecutions;
        },
        enabled: !!user && !!environmentId,
        staleTime: 60_000,
    });
};

export const useListTaskExecutions = (environmentId: string) => {
    return useQuery(useListTaskExecutionsOptions(environmentId));
};

export const useGetTaskExecutionOptions = (id?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    return queryOptions({
        queryKey: environmentAutomationQueryKey.getTaskExecution(id),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const { taskExecution } = await api.environmentAutomationService.getTaskExecution(
                create(GetTaskExecutionRequestSchema, {
                    id,
                }),
            );
            if (!taskExecution) {
                throw new Error("Task execution not found");
            }

            return taskExecution;
        },
        enabled: !!user && !!id,
        staleTime: 60_000,
    });
};

export const useGetTaskExecution = (id?: string) => {
    return useQuery(useGetTaskExecutionOptions(id));
};

export const useStartTask = () => {
    const api = useGitpodAPI();
    return useMutation({
        mutationFn: async (id: string) => {
            const { taskExecution } = await api.environmentAutomationService.startTask(
                create(StartTaskRequestSchema, {
                    id,
                }),
            );
            if (!taskExecution) {
                throw new Error("Task execution not found");
            }
            return taskExecution;
        },
    });
};

function optimisticStopService(client: QueryClient, serviceId: string, environmentId: string) {
    const update = (service?: PlainService) => {
        if (service?.spec && service?.status) {
            service.spec.desiredPhase = ServicePhase.STOPPED;
            service.status.phase = ServicePhase.STOPPING;
        }
    };

    client.setQueryData(environmentAutomationQueryKey.getService(serviceId), (old: PlainService) => update(old));
    client.setQueryData(environmentAutomationQueryKey.listServices(environmentId), (old?: PlainService[]) => {
        const service = (old || []).find((s) => s.id === serviceId);
        update(service);
    });
}

function optimisticStartService(client: QueryClient, serviceId: string, environmentId: string) {
    const update = (service?: PlainService) => {
        if (service?.spec && service?.status) {
            service.spec.desiredPhase = ServicePhase.RUNNING;
            service.status.phase = ServicePhase.STARTING;
        }
    };

    client.setQueryData(environmentAutomationQueryKey.getService(serviceId), (old: PlainService) => update(old));
    client.setQueryData(environmentAutomationQueryKey.listServices(environmentId), (old?: PlainService[]) => {
        const service = (old || []).find((s) => s.id === serviceId);
        update(service);
    });
}

function optimisticStopTaskExecution(client: QueryClient, taskExecutionId: string, environmentId: string) {
    const update = (taskExecution?: PlainTaskExecution) => {
        if (taskExecution?.spec && taskExecution?.status) {
            taskExecution.spec.desiredPhase = TaskExecutionPhase.STOPPED;
        }
    };

    client.setQueryData(environmentAutomationQueryKey.getTaskExecution(taskExecutionId), (old: PlainTaskExecution) =>
        update(old),
    );
    client.setQueryData(
        environmentAutomationQueryKey.listTaskExecutions(environmentId),
        (old?: PlainTaskExecution[]) => {
            const taskExecution = (old || []).find((s) => s.id === taskExecutionId);
            update(taskExecution);
        },
    );
}
